import { useMemo, useState } from "react";
import { displayHost, formatMoney } from "../lib";
import type { SiteProps } from "../types";
import "../styles/land.css";

export default function LandSite({ slots, state, onClaim }: SiteProps) {
  const firstOccupied = slots.find((slot) => slot.entries.length > 0);
  const [selectedId, setSelectedId] = useState(firstOccupied?.id ?? slots[14]?.id ?? slots[0]?.id);
  const selected = useMemo(() => slots.find((slot) => slot.id === selectedId) ?? slots[0], [selectedId, slots]);
  const occupied = slots.filter((slot) => slot.entries.length > 0).length;
  const open = slots.length - occupied;
  const leader = selected?.entries[0];

  function selectParcel(id: string): void {
    setSelectedId(id);
    if (window.matchMedia("(max-width: 780px)").matches) {
      window.requestAnimationFrame(() => {
        document.getElementById("land-parcel-detail")?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    }
  }

  return (
    <main className="land-site">
      <header className="land-header">
        <div className="land-shell land-header-inner">
          <a className="land-wordmark" href="/">outbid<span>.land</span></a>
          <nav aria-label="Main navigation">
            <a href="#parcels">Parcels</a>
            <a href="/rules">Rules</a>
            <a href="https://outbid.earth">Earth</a>
            <a href="https://outbid.shop">Shop</a>
            <a href="https://outbid.homes">Homes</a>
          </nav>
        </div>
      </header>

      <section className="land-hero land-shell">
        <div className="land-status"><i aria-hidden="true" /> {state.stripeEnabled ? `${open} of ${slots.length} parcels open` : "Viewing only"} · from {formatMoney(state.minimumBidCents)}</div>
        <h1>Claim some <em>digital dirt.</em></h1>
        <p>Put your website on a parcel. The highest total owns the sign; every paid URL keeps a ranked spot.</p>
        {selected && (
          <div className="land-hero-action">
            <button type="button" disabled={!state.stripeEnabled} onClick={() => onClaim(selected)}>
              {state.stripeEnabled ? `Join ${selected.label} from ${formatMoney(state.minimumBidCents)}` : "Payments opening soon"}
              <span>→</span>
            </button>
            <a href="#parcels">or pick another parcel</a>
          </div>
        )}
      </section>

      <section className="land-market" id="parcels">
        <div className="land-shell">
          <div className="land-market-head">
            <div><span>Browser County Registry</span><h2>Pick your parcel.</h2></div>
            <p>Click any square. Open land starts at {formatMoney(state.minimumBidCents)}.</p>
          </div>

          <div className="land-workbench">
            <div className="land-map" role="group" aria-label="Digital land parcels">
              {slots.map((slot) => {
                const slotLeader = slot.entries[0];
                const isSelected = slot.id === selected?.id;
                return (
                  <button
                    type="button"
                    key={slot.id}
                    className={`${slotLeader ? "is-developed" : "is-open"} ${isSelected ? "is-selected" : ""}`}
                    onClick={() => selectParcel(slot.id)}
                    aria-pressed={isSelected}
                  >
                    <span>{slot.label.replace("Parcel ", "")}</span>
                    <strong>{slotLeader?.name ?? "OPEN"}</strong>
                    <small>{slotLeader ? formatMoney(slotLeader.amountCents) : formatMoney(state.minimumBidCents)}</small>
                  </button>
                );
              })}
            </div>

            {selected && (
              <aside className="land-deed" id="land-parcel-detail">
                <div className="land-deed-heading">
                  <div><span>Selected parcel</span><h3>{selected.label}</h3></div>
                  <b className={leader ? "is-owned" : "is-open"}>{leader ? "ACTIVE" : "OPEN"}</b>
                </div>

                {leader ? (
                  <div className="land-leader">
                    <span>Current #1</span>
                    <a href={leader.url} target="_blank" rel="noopener noreferrer">{leader.name}</a>
                    <small>{displayHost(leader.url)}</small>
                    <strong>{formatMoney(leader.amountCents)}</strong>
                  </div>
                ) : (
                  <div className="land-leader is-empty">
                    <span>No owner yet</span>
                    <strong>Be first on the deed.</strong>
                  </div>
                )}

                <button className="land-claim" type="button" disabled={!state.stripeEnabled} onClick={() => onClaim(selected)}>
                  {state.stripeEnabled ? `Enter from ${formatMoney(state.minimumBidCents)}` : "Payments opening soon"}<span>→</span>
                </button>
                <p className="land-deed-note">Pay once. Add more later. Getting outbid moves you down—it never erases your paid listing.</p>

                <div className="land-chain">
                  <div className="land-chain-title"><span>Parcel ranking</span><b>{selected.entries.length}</b></div>
                  {selected.entries.length === 0 ? (
                    <p className="land-empty">This ranking is empty.</p>
                  ) : (
                    <ol>
                      {selected.entries.map((entry) => (
                        <li key={entry.id}>
                          <span>#{entry.rank}</span>
                          <div><a href={entry.url} target="_blank" rel="noopener noreferrer">{entry.name}</a><small>{displayHost(entry.url)}</small></div>
                          <b>{formatMoney(entry.amountCents)}</b>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </aside>
            )}
          </div>
        </div>
      </section>

      <footer className="land-footer">
        <div className="land-shell"><b>outbid.land</b><span><a href="/rules">Rules</a> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></span><small>Real money. Fake acreage.</small></div>
      </footer>
    </main>
  );
}
